import React from "react";
import {styled} from "@reside/ui";

import {readableAdmissionStatus} from "../../../../atoms/admission-status-tag/AdmissionStatusTag";
import {WorkflowOption} from "../../../../atoms/start-workflow/types";
import {AdmissionStatusEnum} from "../../../../services/AdmissionsService";

type TriggerSentenceFields = Readonly<{
  initiatingWorkflowType?: string;
  toStatus?: AdmissionStatusEnum | "";
  targetWorkflowTypes?: ReadonlyArray<string>;
}>;

const Blank = styled.strong`
  color: ${({theme}) => theme.color.gray100};
  font-weight: normal;
`;

const getWorkflowLabel = (
  workflowOptions: ReadonlyArray<WorkflowOption>,
  type: string,
) => workflowOptions.find(option => option.type === type)?.label ?? type;

const article = (label: string) => (/^[aeiou]/i.test(label) ? "an" : "a");

const joinLabels = (labels: ReadonlyArray<string>) => {
  if (labels.length <= 1) {
    return labels[0] ?? "";
  }
  if (labels.length === 2) {
    return `${labels[0]} and ${labels[1]}`;
  }
  return `${labels.slice(0, -1).join(", ")}, and ${labels[labels.length - 1]}`;
};

type Props = Readonly<{
  trigger: TriggerSentenceFields;
  workflowOptions: ReadonlyArray<WorkflowOption>;
}>;

export const TriggerSentence = ({trigger, workflowOptions}: Props) => {
  const initiatingLabel = trigger.initiatingWorkflowType
    ? getWorkflowLabel(workflowOptions, trigger.initiatingWorkflowType)
    : undefined;
  const targetWorkflowTypes = trigger.targetWorkflowTypes ?? [];
  const resultingLabels = targetWorkflowTypes.map(type =>
    getWorkflowLabel(workflowOptions, type),
  );
  const isPlural = resultingLabels.length > 1;

  return (
    <>
      When{" "}
      {initiatingLabel ? (
        <>
          {article(initiatingLabel)} <strong>{initiatingLabel}</strong>
        </>
      ) : (
        <Blank>____</Blank>
      )}{" "}
      changes status to{" "}
      {trigger.toStatus ? (
        <strong>{readableAdmissionStatus(trigger.toStatus)}</strong>
      ) : (
        <Blank>____</Blank>
      )}
      , a new{" "}
      {resultingLabels.length > 0 ? (
        <strong>{joinLabels(resultingLabels)}</strong>
      ) : (
        <Blank>____</Blank>
      )}{" "}
      {isPlural ? "workflows will" : "workflow will"} be created.
    </>
  );
};
